import React, { useState, useEffect } from 'react';
import { Star, ChevronLeft, ChevronRight, Quote, CheckCircle, Send, X, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = '/api';

const TestimonialsSection = () => {
  const [reviews, setReviews] = useState([]);
  const [stats, setStats] = useState({ total: 0, average: 0 });
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [showAllReviews, setShowAllReviews] = useState(false);
  const [showReviewForm, setShowReviewForm] = useState(false);
  const [allReviews, setAllReviews] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    rating: 5,
    text: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [featuredRes, statsRes] = await Promise.all([
        axios.get(`${API_URL}/reviews/featured`),
        axios.get(`${API_URL}/reviews/stats`)
      ]);
      setReviews(featuredRes.data);
      setStats(statsRes.data);
    } catch (error) {
      console.error('Error fetching reviews:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAllReviews = async () => {
    try {
      const response = await axios.get(`${API_URL}/reviews`);
      setAllReviews(response.data);
    } catch (error) {
      console.error('Error fetching all reviews:', error);
    }
  };

  const handleShowAll = () => {
    fetchAllReviews();
    setShowAllReviews(true);
  };

  const handleSubmitReview = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.text) {
      toast.error('Prosimo, izpolnite ime in mnenje');
      return;
    }
    
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/reviews`, formData);
      toast.success('Hvala za vaše mnenje!', {
        description: 'Mnenje bo objavljeno po pregledu.'
      });
      setShowReviewForm(false);
      setFormData({ name: '', email: '', rating: 5, text: '' });
    } catch (error) {
      toast.error('Napaka pri pošiljanju mnenja');
    } finally {
      setSubmitting(false);
    }
  };

  const nextReview = () => {
    setCurrentIndex((prev) => (prev + 1) % reviews.length);
  };

  const prevReview = () => {
    setCurrentIndex((prev) => (prev - 1 + reviews.length) % reviews.length);
  };

  // Auto-advance carousel
  useEffect(() => {
    if (reviews.length > 1) {
      const timer = setInterval(() => {
        setCurrentIndex((prev) => (prev + 1) % reviews.length);
      }, 6000);
      return () => clearInterval(timer);
    }
  }, [reviews.length]);

  const StarRating = ({ rating, size = 'md', interactive = false, onChange }) => {
    const sizes = { sm: 'w-4 h-4', md: 'w-5 h-5', lg: 'w-6 h-6' };
    return (
      <div className="flex gap-0.5">
        {[1, 2, 3, 4, 5].map((star) => (
          <button
            key={star}
            type="button"
            disabled={!interactive}
            onClick={() => interactive && onChange?.(star)}
            className={interactive ? 'cursor-pointer hover:scale-110 transition-transform' : 'cursor-default'}
          >
            <Star
              className={`${sizes[size]} ${
                star <= rating
                  ? 'fill-amber-400 text-amber-400'
                  : 'fill-slate-200 text-slate-200'
              }`}
            />
          </button>
        ))}
      </div>
    );
  };

  if (loading) {
    return (
      <section className="section-padding bg-white" id="pricevanja">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center animate-pulse">
            <div className="h-8 bg-slate-200 rounded w-48 mx-auto mb-4" />
            <div className="h-4 bg-slate-200 rounded w-64 mx-auto" />
          </div>
        </div>
      </section>
    );
  }

  return (
    <>
      <section
        id="pricevanja"
        className="section-padding bg-gradient-to-b from-white to-slate-50 overflow-hidden"
        data-testid="testimonials-section"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Section Header with Stats */}
          <div className="text-center mb-12">
            <span className="inline-block text-[#f87171] font-semibold tracking-wider uppercase text-sm mb-4">
              Mnenja pacientov
            </span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#1a365d] mb-4">
              Kaj pravijo o nas
            </h2>
            
            {/* Stats Badge */}
            {stats.total > 0 && (
              <div className="inline-flex items-center gap-3 bg-white rounded-full px-6 py-3 shadow-md border border-slate-100">
                <div className="flex items-center gap-1">
                  <Star className="w-6 h-6 fill-amber-400 text-amber-400" />
                  <span className="text-2xl font-bold text-[#1a365d]">{stats.average}</span>
                </div>
                <div className="w-px h-8 bg-slate-200" />
                <div className="text-left">
                  <p className="text-sm font-semibold text-[#1a365d]">{stats.total} mnenj</p>
                  <p className="text-xs text-slate-500">zadovoljnih pacientov</p>
                </div>
              </div>
            )}
          </div>

          {/* Reviews Display */}
          {reviews.length > 0 ? (
            <>
              {/* Desktop: Grid of 3 cards */}
              <div className="hidden md:grid md:grid-cols-3 gap-6 mb-10">
                {reviews.slice(0, 3).map((review, idx) => (
                  <div
                    key={review.id || idx}
                    className="bg-white rounded-2xl p-6 shadow-lg border border-slate-100 hover:shadow-xl transition-shadow"
                    data-testid={`review-card-${idx}`}
                  >
                    {/* Quote Icon */}
                    <div className="w-10 h-10 bg-[#f87171]/10 rounded-full flex items-center justify-center mb-4">
                      <Quote className="w-5 h-5 text-[#f87171]" />
                    </div>
                    
                    {/* Rating */}
                    <StarRating rating={review.rating} size="sm" />
                    
                    {/* Text */}
                    <p className="text-slate-600 mt-4 mb-4 line-clamp-4 leading-relaxed">
                      "{review.text}"
                    </p>
                    
                    {/* Author */}
                    <div className="flex items-center justify-between pt-4 border-t border-slate-100">
                      <div>
                        <p className="font-semibold text-[#1a365d]">{review.name}</p>
                        {review.service_name && (
                          <p className="text-xs text-slate-500">{review.service_name}</p>
                        )}
                      </div>
                      {review.is_verified && (
                        <div className="flex items-center gap-1 text-emerald-600 text-xs">
                          <CheckCircle className="w-4 h-4" />
                          <span>Verificirano</span>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {/* Mobile: Carousel */}
              <div className="md:hidden relative">
                <div className="bg-white rounded-2xl p-6 shadow-lg border border-slate-100">
                  {/* Quote Icon */}
                  <div className="absolute -top-4 left-6 w-10 h-10 bg-[#f87171] rounded-full flex items-center justify-center shadow-md">
                    <Quote className="w-5 h-5 text-white" />
                  </div>
                  
                  {/* Rating */}
                  <div className="pt-2 mb-4">
                    <StarRating rating={reviews[currentIndex]?.rating || 5} />
                  </div>
                  
                  {/* Text */}
                  <p className="text-slate-600 text-lg leading-relaxed mb-6">
                    "{reviews[currentIndex]?.text}"
                  </p>
                  
                  {/* Author */}
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-semibold text-[#1a365d]">{reviews[currentIndex]?.name}</p>
                      {reviews[currentIndex]?.service_name && (
                        <p className="text-xs text-slate-500">{reviews[currentIndex]?.service_name}</p>
                      )}
                    </div>
                    {reviews[currentIndex]?.is_verified && (
                      <div className="flex items-center gap-1 text-emerald-600 text-xs">
                        <CheckCircle className="w-4 h-4" />
                        <span>Verificirano</span>
                      </div>
                    )}
                  </div>
                </div>
                
                {/* Navigation */}
                {reviews.length > 1 && (
                  <>
                    <div className="flex justify-center gap-2 mt-6">
                      {reviews.map((_, index) => (
                        <button
                          key={index}
                          onClick={() => setCurrentIndex(index)}
                          className={`h-2 rounded-full transition-all ${
                            index === currentIndex ? 'bg-[#f87171] w-6' : 'bg-slate-300 w-2'
                          }`}
                        />
                      ))}
                    </div>
                    
                    <div className="absolute top-1/2 -translate-y-1/2 left-0 right-0 flex justify-between px-2 pointer-events-none">
                      <Button
                        variant="outline"
                        size="icon"
                        onClick={prevReview}
                        className="pointer-events-auto rounded-full bg-white/80 backdrop-blur-sm shadow-md border-0 w-10 h-10"
                      >
                        <ChevronLeft className="w-5 h-5" />
                      </Button>
                      <Button
                        variant="outline"
                        size="icon"
                        onClick={nextReview}
                        className="pointer-events-auto rounded-full bg-white/80 backdrop-blur-sm shadow-md border-0 w-10 h-10"
                      >
                        <ChevronRight className="w-5 h-5" />
                      </Button>
                    </div>
                  </>
                )}
              </div>
            </>
          ) : (
            /* No reviews yet - CTA to leave first review */
            <div className="text-center py-12 bg-white rounded-2xl shadow-sm border border-slate-100">
              <Quote className="w-12 h-12 text-slate-200 mx-auto mb-4" />
              <p className="text-slate-500 mb-4">Bodite prvi, ki deli svoje izkušnje!</p>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mt-10">
            {stats.total > 3 && (
              <Button
                variant="outline"
                className="rounded-full px-8 py-6 text-[#1a365d] border-[#1a365d] hover:bg-[#1a365d] hover:text-white"
                onClick={handleShowAll}
                data-testid="show-all-reviews"
              >
                Vsa mnenja ({stats.total})
              </Button>
            )}
            <Button
              className="rounded-full px-8 py-6 bg-[#f87171] hover:bg-[#f87171]/90 text-white"
              onClick={() => setShowReviewForm(true)}
              data-testid="write-review-btn"
            >
              <Send className="w-5 h-5 mr-2" />
              Oddajte mnenje
            </Button>
          </div>

          {/* Trust Indicators */}
          <div className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
            {[
              { value: '1000+', label: 'Zadovoljnih pacientov' },
              { value: '15+', label: 'Let izkušenj' },
              { value: stats.average > 0 ? stats.average.toString() : '5.0', label: 'Povprečna ocena' },
              { value: '98%', label: 'Priporoča nas' },
            ].map((stat, index) => (
              <div 
                key={index} 
                className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100"
                data-testid={`trust-stat-${index}`}
              >
                <div className="text-3xl md:text-4xl font-bold text-[#1a365d]">{stat.value}</div>
                <div className="text-slate-500 text-sm mt-2">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* All Reviews Modal */}
      {showAllReviews && (
        <div 
          className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
          onClick={() => setShowAllReviews(false)}
        >
          <div 
            className="bg-white rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-hidden shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="sticky top-0 bg-[#1a365d] text-white p-4 flex items-center justify-between">
              <div>
                <h3 className="font-bold text-lg">Mnenja pacientov</h3>
                <p className="text-white/70 text-sm">{stats.total} mnenj • Povprečna ocena {stats.average}</p>
              </div>
              <button 
                onClick={() => setShowAllReviews(false)}
                className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center hover:bg-white/30"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="p-4 overflow-y-auto max-h-[calc(90vh-80px)] space-y-4">
              {allReviews.map((review, idx) => (
                <div key={review.id || idx} className="bg-slate-50 rounded-xl p-4">
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <p className="font-semibold text-[#1a365d]">{review.name}</p>
                      {review.service_name && (
                        <p className="text-xs text-slate-500">{review.service_name}</p>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <StarRating rating={review.rating} size="sm" />
                      {review.is_verified && (
                        <div className="flex items-center gap-1 text-emerald-600 text-xs bg-emerald-50 px-2 py-1 rounded-full">
                          <CheckCircle className="w-3 h-3" />
                          <span>Verificirano</span>
                        </div>
                      )}
                    </div>
                  </div>
                  <p className="text-slate-600">{review.text}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Review Form Modal */}
      {showReviewForm && (
        <div 
          className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
          onClick={() => setShowReviewForm(false)}
        >
          <div 
            className="bg-white rounded-2xl max-w-lg w-full shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="bg-[#1a365d] text-white p-4 rounded-t-2xl flex items-center justify-between">
              <h3 className="font-bold text-lg">Oddajte mnenje</h3>
              <button 
                onClick={() => setShowReviewForm(false)}
                className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center hover:bg-white/30"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <form onSubmit={handleSubmitReview} className="p-6 space-y-4">
              {/* Rating */}
              <div>
                <label className="block text-sm font-medium text-[#1a365d] mb-2">Vaša ocena *</label>
                <div className="flex items-center gap-2">
                  <StarRating 
                    rating={formData.rating} 
                    size="lg" 
                    interactive 
                    onChange={(rating) => setFormData({ ...formData, rating })}
                  />
                  <span className="text-slate-500 text-sm ml-2">
                    {formData.rating === 5 ? 'Odlično!' : 
                     formData.rating === 4 ? 'Zelo dobro' :
                     formData.rating === 3 ? 'Dobro' :
                     formData.rating === 2 ? 'Zadovoljivo' : 'Slabo'}
                  </span>
                </div>
              </div>
              
              {/* Name */}
              <div>
                <label className="block text-sm font-medium text-[#1a365d] mb-2">Vaše ime *</label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Ime in priimek"
                  className="rounded-xl"
                  required
                />
              </div>
              
              {/* Email */}
              <div>
                <label className="block text-sm font-medium text-[#1a365d] mb-2">E-pošta (opcijsko)</label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="vasa@email.si"
                  className="rounded-xl"
                />
                <p className="text-xs text-slate-500 mt-1">Za obveščanje o objavi mnenja</p>
              </div>
              
              {/* Review Text */}
              <div>
                <label className="block text-sm font-medium text-[#1a365d] mb-2">Vaše mnenje *</label>
                <Textarea
                  value={formData.text}
                  onChange={(e) => setFormData({ ...formData, text: e.target.value })}
                  placeholder="Opišite vašo izkušnjo..."
                  rows={4}
                  className="rounded-xl resize-none"
                  required
                />
              </div>
              
              <Button
                type="submit"
                disabled={submitting}
                className="w-full rounded-full py-6 bg-[#f87171] hover:bg-[#f87171]/90 text-white font-semibold"
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Pošiljam...
                  </>
                ) : (
                  <>
                    <Send className="w-5 h-5 mr-2" />
                    Pošlji mnenje
                  </>
                )}
              </Button>
              
              <p className="text-xs text-slate-500 text-center">
                Mnenje bo objavljeno po pregledu.
              </p>
            </form>
          </div>
        </div>
      )}
    </>
  );
};

export default TestimonialsSection;
